import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { labelForPath } from '@/lib/nav';

// Tracks the in-app navigation trail so the back button can name (and return
// to) the page you were actually on previously — independent of the static
// page hierarchy.

interface Entry {
  pathname: string;
  search: string;
  key: string;
}

interface PreviousPage {
  pathname: string;
  search: string;
  label: string;
}

const NavHistoryContext = createContext<{ previous: PreviousPage | null }>({ previous: null });

export function NavHistoryProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navType = useNavigationType();
  const stackRef = useRef<Entry[]>([]);
  const [, bump] = useState(0);

  useEffect(() => {
    const cur: Entry = { pathname: location.pathname, search: location.search, key: location.key };
    const s = stackRef.current;
    if (s.length === 0) {
      s.push(cur);
    } else if (navType === 'POP') {
      // Browser back/forward: pop if we returned to the prior entry.
      if (s.length >= 2 && s[s.length - 2].key === cur.key) {
        s.pop();
      } else if (s[s.length - 1].key !== cur.key) {
        s.push(cur);
      }
    } else if (navType === 'REPLACE') {
      s[s.length - 1] = cur;
    } else {
      s.push(cur);
    }
    bump((v) => v + 1);
  }, [location.key, location.pathname, location.search, navType]);

  const s = stackRef.current;
  const prev = s.length >= 2 ? s[s.length - 2] : null;
  const previous: PreviousPage | null = prev
    ? { pathname: prev.pathname, search: prev.search, label: labelForPath(prev.pathname, prev.search) }
    : null;

  return <NavHistoryContext.Provider value={{ previous }}>{children}</NavHistoryContext.Provider>;
}

export function useNavHistory() {
  return useContext(NavHistoryContext);
}
